/**
 * The text behind `MoonNumericField`: what a keystroke may leave in the box,
 * how the box reads as a number, and how a number is shown back.
 *
 * French writes twelve and a half as "12,5" and its keyboards put a comma on
 * the decimal key, so with `decimalComma` a comma is a decimal point too.
 * English uses the comma to group thousands, so there it is dropped.
 */

export function cleanNumericInput(
  text: string,
  decimals: number,
  decimalComma: boolean,
): string {
  // Keep digits, a leading minus and (when decimals > 0) one decimal point.
  const allowed =
    decimals === 0 ? /[^0-9-]/g : decimalComma ? /[^0-9.,-]/g : /[^0-9.-]/g;
  let cleaned = text.replace(allowed, "");
  // Minus only legal as a leading char, so the buffer can't drift into
  // "12-3", which parseFloat would silently cut to 12.
  if (cleaned.indexOf("-") > 0) {
    cleaned = cleaned[0] + cleaned.slice(1).replace(/-/g, "");
  }
  if (decimals > 0) {
    // At most one decimal point. Drop any extras after the first.
    const firstPoint = cleaned.search(/[.,]/);
    if (firstPoint !== -1) {
      cleaned =
        cleaned.slice(0, firstPoint + 1) +
        cleaned.slice(firstPoint + 1).replace(/[.,]/g, "");
    }
  }
  return cleaned;
}

export function formatForDisplay(
  value: number | null,
  decimals: number,
  decimalComma: boolean,
): string {
  if (value === null || Number.isNaN(value)) return "";
  if (decimals === 0) return String(Math.trunc(value));
  const fixed = value.toFixed(decimals);
  return decimalComma ? fixed.replace(".", ",") : fixed;
}

export function parseBuffer(buffer: string, decimals: number): number | null {
  // The buffer only holds a comma where it is the decimal point (French).
  const trimmed = buffer.trim().replace(",", ".");
  if (trimmed === "") return null;
  const parsed = decimals > 0 ? parseFloat(trimmed) : parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}
